import { Tooltip as AntdTooltip } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { numFormat } from "tools/string";

export interface IRenderBarChart {
  data: any[];
  dataKeyX: string;
  dataKeyY: string;
  colorsBar?: string[];
  color?: string;
  labelX?: string;
  labelY?: string;
  height?: number;
  isSla?: boolean;
  isMenu?: boolean;
}

export const RenderBarChart: FC<IRenderBarChart> = ({
  data,
  dataKeyX,
  dataKeyY,
  colorsBar,
  color = "#80BC28",
  labelX,
  labelY,
  isSla,
  isMenu,
  height = 300,
}) => {
  const ref = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const [maxTickLength, setMaxTickLength] = useState(12);

  useEffect(() => {
    if (ref?.current === null) return;
    setWidth(ref?.current?.offsetWidth);
    const getwidth = () => {
      setWidth(ref?.current?.offsetWidth);
    };
    window.addEventListener("resize", getwidth);
    return () => window.removeEventListener("resize", getwidth);
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (ref?.current) {
        setWidth(ref.current.offsetWidth);
      }

      const screenWidth = window.innerWidth;

      if (isMenu) {
        if (screenWidth <= 1280) {
          setMaxTickLength(7); 
        } else {
          setMaxTickLength(12);
        }
      } else {
        setMaxTickLength(12);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [isMenu]);

  const tickFormatter = (value: string, index: number) => {
    const number = numFormat(Number(value));
    if (!number) return "0";
    return number?.toString();
  };

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const textAnchor = "middle";

    let tickValue = payload.value;
    let fullValue = payload.value;

    if (tickValue.length > maxTickLength) {
      tickValue = tickValue.slice(0, maxTickLength) + "...";
    }
    return (
      <g transform={`translate(${x},${y})`}>
         <foreignObject x={-50} y={0} width={100} height={30}>
          <AntdTooltip title={fullValue}>
            <text x={0} y={0} dy={16} textAnchor={textAnchor}>
              <div
                style={{
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {tickValue}
              </div>
            </text>
          </AntdTooltip>
        </foreignObject>

      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-5 rounded-app">
          <p className="label">{`${payload[0].value.toLocaleString()}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={ref} className=" overflow-auto !overflow-x-hidden">
      <BarChart
        width={width}
        height={height}
        data={data}
        barSize={50}
        margin={{
          top: 5,
          right: 30,
          left: 0,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="0 0" vertical={false} />
        <XAxis
          dataKey={dataKeyX}
          label={
            labelX ? (
              <Label value={labelX} offset={0} position="insideBottom" />
            ) : undefined
          }
          interval={0}
          tickSize={0}
          tickMargin={10}
          tick={<CustomXAxisTick />}
        />
        <YAxis
          tickFormatter={!isSla ? tickFormatter : (value) => `${value}%`}
          tickCount={
            isSla
              ? 10
              : data.length > 0 &&
                Math.max(...data.map((item) => item[dataKeyY])) < 10
              ? 10
              : undefined
          }
          ticks={isSla ? [10, 20, 30, 40, 50, 60, 70, 80, 90, 100] : undefined}
          domain={
            !isSla
              ? [
                  0,
                  (dataMax: number) => (dataMax < 10 ? 10 : Math.ceil(dataMax)),
                ]
              : [0, 100]
          }
          label={
            !isSla
              ? labelY
                ? { value: labelY, angle: -90, position: "center" }
                : undefined
              : undefined
          }
        />
        <Tooltip content={CustomTooltip} />
        <Bar dataKey={dataKeyY} fill={"#d40027"} barSize={50}>
          {data.map((item, index) => {
            const fill = colorsBar
              ? colorsBar[index % 20]
              : item.isLowerThreshold !== undefined
              ? item.isLowerThreshold
                ? "#d40027"
                : color
              : color;
            return <Cell key={`cell-${index}`} fill={fill} />;
          })}
        </Bar>
      </BarChart>

      {isSla && (
        <div
          style={{
            position: "absolute",
            top: "19.60%",
            left: 60,
            right: 0,
            height: "1px",
            backgroundColor: "#FF0000",
            transform: "translateY(-50%)",
          }}
        />
      )}
    </div>
  );
};
